import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ComposedChart,
    Line,
    LineChart,
    Area,
    AreaChart,
} from 'recharts';
import {
    Class as ClassIcon,
    People as PeopleIcon,
    School as SchoolIcon,
    Timer as TimerIcon,
    AttachMoney as AttachMoneyIcon,
    CheckCircle as CheckCircleIcon,
    TrendingUp as TrendingUpIcon,
    Schedule as ScheduleIcon,
    CalendarMonth as CalendarMonthIcon,
    EmojiEvents as EmojiEventsIcon,
    GridView as GridViewIcon,
    CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import useFetch from '../../../../hooks/useFetch';
import RTL from '../../../../utils/RTL';
import StandardTable from '../../../common/table/StandardTable';
import { toPersianDigits } from '../../../../utils/convertNumbers';
import { API } from '../../../../utils/apiRoutes';
import PersianMonthPicker from '../../../common/PersianMonthPicker';
import moment from 'moment-jalaali';

// Chart views grouped for the navigation rail. `indices` map to the fixed activeTab
// indices the chart blocks switch on.
const VIEW_GROUPS = [
    { title: 'زمان‌بندی و تراکم', indices: [0, 1, 2, 7] },
    { title: 'عملکرد و درآمد', indices: [4, 5, 6, 9] },
    { title: 'روند و جمعیت', indices: [3, 8] },
];

// Returns a moment for the first day of the Jalali month containing `date`, or null.
// The pickers return a Gregorian Date representing a Jalali month selection, so we always
// snap to the first day of the *selected Jalali month* (setting day=1 on the Gregorian date
// can shift the Jalali month backward, e.g. Farvardin -> Esfand).
const jalaliMonthStart = (date) => {
    if (!date) return null;
    const m = moment(date);
    const first = moment(
        `${m.jYear()}/${String(m.jMonth() + 1).padStart(2, '0')}/01`,
        'jYYYY/jMM/jDD',
        true,
    );
    return first.isValid() ? first : null;
};

// First day of the current Jalali month, as a Date (for initial picker state).
const currentJalaliMonthStart = () => {
    const first = jalaliMonthStart(new Date());
    const d = first ? first.toDate() : new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

// First day of the Jalali month `n` months before the current one, as a Date.
const jalaliMonthStartMonthsAgo = (n) => {
    const first = jalaliMonthStart(new Date());
    const d = first ? first.clone().subtract(n, 'jMonth').toDate() : new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

function ClassroomInfoGraph() {
    const theme = useTheme();

    // Curated, muted chart palette harmonized with the teal/earth theme (recharts series
    // need categorical colors; we lead with theme tokens + a few intentional accents).
    const chartColors = React.useMemo(() => [
        theme.palette.primary.main,    // teal
        theme.palette.action.active,   // clay
        '#A98B5D',                     // warm gold
        theme.palette.error.main,      // terracotta
        theme.palette.success.main,    // sage
        '#5B7C99',                     // slate
        '#7A6A8A',                     // muted plum
    ], [theme]);

    // Theme recharts globally via CSS so axis text, gridlines, legend and the tooltip
    // adapt to light/dark (recharts' defaults are light-mode only — the tooltip and axis
    // labels are unreadable in dark mode otherwise).
    const chartSx = React.useMemo(() => ({
        '& .recharts-cartesian-axis-tick-value': { fill: theme.palette.text.default },
        // Numeric Y-axis labels render left-to-right so grouped numbers (e.g. ۲۰٬۰۰۰٬۰۰۰)
        // don't get bidi-reordered/jumbled inside the RTL container.
        '& .recharts-yAxis .recharts-cartesian-axis-tick-value': { direction: 'ltr' },
        '& .recharts-cartesian-grid line': { stroke: alpha(theme.palette.text.default, 0.12) },
        '& .recharts-cartesian-axis line, & .recharts-cartesian-axis-line': { stroke: alpha(theme.palette.text.default, 0.2) },
        '& .recharts-legend-item-text': { color: `${theme.palette.text.default} !important` },
        '& .recharts-default-tooltip': {
            backgroundColor: `${theme.palette.background.default} !important`,
            border: `1px solid ${theme.palette.border.main} !important`,
            borderRadius: '8px',
            color: theme.palette.text.default,
        },
        '& .recharts-tooltip-label': { color: theme.palette.text.default },
        '& .recharts-tooltip-item': { color: `${theme.palette.text.default} !important` },
    }), [theme]);

    // Heatmap cell styling: single-hue teal intensity ramp (replaces the green→red rainbow).
    const heatmapCell = React.useCallback((count, maxCount) => {
        if (!count || maxCount === 0) {
            return { bg: alpha(theme.palette.text.default, 0.04), color: alpha(theme.palette.text.default, 0.45) };
        }
        const intensity = count / maxCount;
        return {
            bg: alpha(theme.palette.primary.main, 0.12 + 0.8 * intensity),
            color: intensity > 0.55 ? theme.palette.primary.contrastText : theme.palette.text.default,
        };
    }, [theme]);
    const [activeTab, setActiveTab] = React.useState(0);
    // Range is month-granular: from the first day of `fromMonth` to the last day of `toMonth`.
    // Defaults to the last 3 months up to and including the current Jalali month.
    const [fromMonth, setFromMonth] = React.useState(() => jalaliMonthStartMonthsAgo(3));
    const [toMonth, setToMonth] = React.useState(currentJalaliMonthStart);

    // Calculate start/end dates: first day of `fromMonth` Jalali month -> last day of `toMonth`.
    const { startDate, endDate } = React.useMemo(() => {
        const from = jalaliMonthStart(fromMonth);
        const to = jalaliMonthStart(toMonth);
        if (!from || !to) {
            return { startDate: null, endDate: null };
        }

        const startDate = from.toDate();
        startDate.setHours(0, 0, 0, 0);

        // Last day of the `toMonth` Jalali month: go to next month and subtract 1 day.
        const lastDayJalali = to.clone().add(1, 'jMonth').subtract(1, 'day');
        if (!lastDayJalali.isValid()) {
            return { startDate, endDate: startDate };
        }
        const endDate = lastDayJalali.toDate();
        endDate.setHours(23, 59, 59, 999);

        return { startDate, endDate };
    }, [fromMonth, toMonth]);

    // Keep the range valid: `fromMonth` must never come after `toMonth`. The pickers store
    // first-day-of-Jalali-month Dates, so a plain getTime() comparison orders them by month.
    const handleFromChange = React.useCallback((date) => {
        const start = jalaliMonthStart(date);
        if (!start) return;
        const d = start.toDate();
        d.setHours(0, 0, 0, 0);
        setFromMonth(d);
        setToMonth((prev) => (prev && d.getTime() > new Date(prev).getTime() ? d : prev));
    }, []);

    const handleToChange = React.useCallback((date) => {
        const start = jalaliMonthStart(date);
        if (!start) return;
        const d = start.toDate();
        d.setHours(0, 0, 0, 0);
        setToMonth(d);
        setFromMonth((prev) => (prev && d.getTime() < new Date(prev).getTime() ? d : prev));
    }, []);

    // Convert dates to strings for API
    const startDateStr = React.useMemo(() => {
        if (!startDate) return null;
        return startDate instanceof Date 
            ? startDate.toISOString().split('T')[0] 
            : startDate;
    }, [startDate]);

    const endDateStr = React.useMemo(() => {
        if (!endDate) return null;
        return endDate instanceof Date 
            ? endDate.toISOString().split('T')[0] 
            : endDate;
    }, [endDate]);

    // Build API params for enhanced classroom info graph (selected month range).
    const apiParams = React.useMemo(() => {
        const params = {};
        if (startDateStr) {
            params.start_date = startDateStr;
        }
        if (endDateStr) {
            params.end_date = endDateStr;
        }
        return params;
    }, [startDateStr, endDateStr]);

    // Fetch data with date filters
    const { data, loading, error } = useFetch(API.enhancedClassroomInfoGraph, { params: apiParams });

    // Seasonal trends share the same selected range; the monthly trend chart spans it.
    const { data: seasonalData } = useFetch(API.seasonalTrends, { params: apiParams });

    // Filter and sort monthly data to the selected range (fromMonth .. toMonth, inclusive).
    const filteredMonthlyData = React.useMemo(() => {
        if (!seasonalData?.seasonal_trends?.monthly || !fromMonth || !toMonth) {
            return [];
        }

        const fromMoment = moment(fromMonth);
        const fromJalaliYear = fromMoment.jYear();
        const fromJalaliMonth = fromMoment.jMonth() + 1;
        const toMoment = moment(toMonth);
        const toJalaliYear = toMoment.jYear();
        const toJalaliMonth = toMoment.jMonth() + 1;

        // Backend may now return jalali_year/jalali_month/jalali_month_name.
        // Fallback: derive Jalali month from Gregorian (day 15) for older servers.
        const filtered = seasonalData.seasonal_trends.monthly.filter(item => {
            const itemJalaliYear = Number.isFinite(item.jalali_year) ? item.jalali_year : (() => {
                const itemMiddleDay = new Date(item.year, item.month - 1, 15);
                return moment(itemMiddleDay).jYear();
            })();
            const itemJalaliMonth = Number.isFinite(item.jalali_month) ? item.jalali_month : (() => {
                const itemMiddleDay = new Date(item.year, item.month - 1, 15);
                return moment(itemMiddleDay).jMonth() + 1;
            })();

            // Check if this Jalali month is within the selected range (fromMonth .. toMonth)
            const isAfterStart = itemJalaliYear > fromJalaliYear ||
                                 (itemJalaliYear === fromJalaliYear && itemJalaliMonth >= fromJalaliMonth);
            const isBeforeEnd = itemJalaliYear < toJalaliYear ||
                               (itemJalaliYear === toJalaliYear && itemJalaliMonth <= toJalaliMonth);

            return isAfterStart && isBeforeEnd;
        });
        
        // Sort by year and month
        const sorted = filtered
            .map(item => ({
                ...item,
                // prefer backend jalali labels; fallback to computed jalali label
                month_name: item.jalali_month_name || item.month_name,
                _jalali_year: Number.isFinite(item.jalali_year) ? item.jalali_year : (() => {
                    const d = new Date(item.year, item.month - 1, 15);
                    return moment(d).jYear();
                })(),
                _jalali_month: Number.isFinite(item.jalali_month) ? item.jalali_month : (() => {
                    const d = new Date(item.year, item.month - 1, 15);
                    return moment(d).jMonth() + 1;
                })(),
            }))
            .sort((a, b) => (a._jalali_year - b._jalali_year) || (a._jalali_month - b._jalali_month));

        return sorted;
    }, [seasonalData, fromMonth, toMonth]);

    // Tab definitions. `enabled` is false when there's no data for that view in the
    // selected month — those tabs are hidden (not greyed). Index order is fixed because
    // the chart blocks below switch on `activeTab === <index>`.
    const tabs = React.useMemo(() => [
        { label: 'دوره‌ها بر اساس مدت جلسه', icon: <TimerIcon />, enabled: Boolean(data?.courses_by_duration?.length) },
        { label: 'ثبت‌نام بر اساس ساعت', icon: <ScheduleIcon />, enabled: Boolean(data?.classes_by_hour?.length) },
        { label: 'ثبت‌نام بر اساس روز هفته', icon: <CalendarMonthIcon />, enabled: Boolean(data?.classes_by_day?.length) },
        { label: 'روند ماهانه', icon: <TrendingUpIcon />, enabled: Boolean(filteredMonthlyData?.length) },
        { label: 'درآمد هر دوره', icon: <AttachMoneyIcon />, enabled: Boolean(data?.revenue_by_course?.length) },
        { label: 'نرخ حضور هر دوره', icon: <CheckCircleIcon />, enabled: Boolean(data?.attendance_by_course?.length) },
        { label: 'پرطرفدارترین دوره‌ها', icon: <EmojiEventsIcon />, enabled: Boolean(data?.top_courses_by_enrollment?.length) },
        { label: 'نقشهٔ تراکم هفتگی', icon: <GridViewIcon />, enabled: Boolean(data?.schedule_heatmap?.length) },
        { label: 'توزیع سنی', icon: <PeopleIcon />, enabled: Boolean(data?.age_distribution?.overall?.length) },
        { label: 'مقایسهٔ دوره‌های مشابه', icon: <CompareArrowsIcon />, enabled: Boolean(data?.similar_courses_comparison?.length) },
    ], [data, filteredMonthlyData]);

    // If the active tab has no data (e.g. after changing month), jump to the first that does.
    React.useEffect(() => {
        if (tabs.length && !tabs[activeTab]?.enabled) {
            const first = tabs.findIndex((t) => t.enabled);
            if (first !== -1 && first !== activeTab) setActiveTab(first);
        }
    }, [tabs, activeTab]);

    // Show error only if we don't have data
    if (error && !data) {
        return (
            <RTL>
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            </RTL>
        );
    }

    // Show loading
    if (loading && !data) {
        return (
            <RTL>
                <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
            </RTL>
        );
    }

    // Show info only if we don't have data and not loading
    if (!data && !loading) {
        return (
            <RTL>
                <Box sx={{ p: 3 }}>
                    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>داده‌ای برای نمایش وجود ندارد.</Alert>
                </Box>
            </RTL>
        );
    }

    return (
        <RTL>
            <Box sx={{ p: 3, position: 'relative', maxWidth: 1400, mx: 'auto' }}>
                {/* Error Alert */}
                {error && data && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Loading Indicator - Only show when we have existing data (refreshing) */}
                {loading && data && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 16,
                            right: 16,
                            zIndex: 9999,
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            p: 1.5,
                            boxShadow: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <CircularProgress size={24} />
                        <Typography variant="body2">در حال به‌روزرسانی...</Typography>
                    </Box>
                )}

                <Typography variant="h4" sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
                    داده نگار کلاس‌ها
                </Typography>

                {/* Month range selection — sticky so the pickers stay reachable while scrolling */}
                <Paper
                    sx={{
                        p: 3,
                        mb: 3,
                        position: 'sticky',
                        // The dashboard scroll container pads 1rem on top; pull up so the bar
                        // pins flush to the viewport (matches the آمار سامانه filter bar).
                        top: '-1rem',
                        // Above the heatmap's sticky cells (zIndex 10) and table headers (3)
                        // so they scroll underneath instead of over the bar.
                        zIndex: 20,
                        bgcolor: 'background.default',
                        borderColor: 'border.main',
                    }}
                >
                    <Grid 
                        container 
                        spacing={2}
                        alignItems="center"
                        sx={{
                            '& .MuiTextField-root': { m: 0 },
                            '& .MuiFormControl-root': { m: 0 },
                        }}
                    >
                        <Grid item xs={12} sm={6} md={3}>
                            <PersianMonthPicker
                                label="از ماه"
                                value={fromMonth ? new Date(fromMonth) : null}
                                onChange={handleFromChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <PersianMonthPicker
                                label="تا ماه"
                                value={toMonth ? new Date(toMonth) : null}
                                onChange={handleToChange}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="body2" color="text.secondary">
                                    بازه انتخاب‌شده:
                                </Typography>
                                {fromMonth && toMonth && (
                                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                        {`از ${toPersianDigits(moment(fromMonth).format('jMMMM jYYYY'))} تا ${toPersianDigits(moment(toMonth).format('jMMMM jYYYY'))}`}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {!tabs.some((t) => t.enabled) ? (
                    <Alert severity="info" variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                        برای بازه انتخاب‌شده داده‌ای برای نمایش وجود ندارد.
                    </Alert>
                ) : (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>
                    {/* View navigation: grouped vertical rail (desktop) / dropdown (mobile) */}
                    <Box sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0 }}>
                        <FormControl fullWidth size="small" sx={{ display: { md: 'none' }, mb: 1 }}>
                            <InputLabel>نمای داده</InputLabel>
                            <Select
                                value={tabs[activeTab]?.enabled ? activeTab : ''}
                                label="نمای داده"
                                onChange={(e) => setActiveTab(e.target.value)}
                            >
                                {VIEW_GROUPS.flatMap((g) => {
                                    const items = g.indices.filter((i) => tabs[i]?.enabled);
                                    if (!items.length) return [];
                                    return [
                                        <ListSubheader key={`h-${g.title}`}>{g.title}</ListSubheader>,
                                        ...items.map((i) => (
                                            <MenuItem key={i} value={i}>{tabs[i].label}</MenuItem>
                                        )),
                                    ];
                                })}
                            </Select>
                        </FormControl>
                        <Paper sx={{ display: { xs: 'none', md: 'block' }, p: 1, bgcolor: 'background.default', borderColor: 'border.main' }}>
                            <List dense disablePadding>
                                {VIEW_GROUPS.map((g) => {
                                    const items = g.indices.filter((i) => tabs[i]?.enabled);
                                    if (!items.length) return null;
                                    return (
                                        <React.Fragment key={g.title}>
                                            <ListSubheader disableSticky sx={{ bgcolor: 'transparent', fontWeight: 'bold', color: 'text.secondary', lineHeight: 2.4 }}>
                                                {g.title}
                                            </ListSubheader>
                                            {items.map((i) => (
                                                <ListItemButton
                                                    key={i}
                                                    selected={activeTab === i}
                                                    onClick={() => setActiveTab(i)}
                                                    sx={{ borderRadius: 1, mb: 0.25 }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 36, color: activeTab === i ? 'primary.main' : 'text.secondary' }}>
                                                        {tabs[i].icon}
                                                    </ListItemIcon>
                                                    <ListItemText primary={tabs[i].label} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                                                </ListItemButton>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        </Paper>
                    </Box>

                    {/* Chart content */}
                    <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <Grid container spacing={3} justifyContent="center" sx={chartSx}>
                    {/* Courses by Duration - Bar Chart */}
                    {activeTab === 0 && data?.courses_by_duration && data.courses_by_duration.length > 0 && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                    <TimerIcon sx={{ ml: 1 }} />
                                    مدرس - دوره بر اساس مدت زمان
                                </Typography>
                                <ResponsiveContainer width="100%" height={550}>
                                    <BarChart 
                                        data={data.courses_by_duration}
                                        margin={{ top: 20, right: 30, left: 80, bottom: 100 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="duration"
                                            angle={-90}
                                            textAnchor="start"
                                            height={100}
                                            dy={5}
                                            interval={0}
                                            tickFormatter={(value) => {
                                                // Extract number from "30 دقیقه" format and convert to Persian
                                                const match = value.match(/(\d+)/);
                                                if (match) {
                                                    const number = parseInt(match[1]);
                                                    return `${toPersianDigits(number)} دقیقه`;
                                                }
                                                return value;
                                            }}
                                        />
                                        <YAxis 
                                            tickFormatter={(value) => toPersianDigits(value)}
                                            width={92}
                                            tickMargin={24}
                                        />
                                        <Tooltip formatter={(value) => toPersianDigits(value)} />
                                        <Legend />
                                        <Bar dataKey="count" fill={chartColors[1]} name="تعداد کلاس" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Paper>
                        </Grid>
                    )}

                    {/* Classes by Hour - Bar Chart */}
                    {activeTab === 1 && data?.classes_by_hour && data.classes_by_hour.length > 0 && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                    <TimerIcon sx={{ ml: 1 }} />
                                    تعداد هنرجو-دوره بر اساس ساعت روز در کل هفته 
                                </Typography>
                                <ResponsiveContainer width="100%" height={550}>
                                    <BarChart 
                                        data={data.classes_by_hour}
                                        margin={{ top: 20, right: 30, left: 80, bottom: 100 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="hour"
                                            tickFormatter={(value) => `${toPersianDigits(value)}:۰۰`}
                                            angle={-90}
                                            textAnchor="start"
                                            height={100}
                                            dy={5}
                                            interval={0}
                                        />
                                        <YAxis 
                                            tickFormatter={(value) => toPersianDigits(value)}
                                            width={92}
                                            tickMargin={24}
                                        />
                                        <Tooltip formatter={(value) => toPersianDigits(value)} />
                                        <Legend />
                                        <Bar dataKey="count" fill={chartColors[0]} name="تعداد هنرجو-دوره" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Paper>
                        </Grid>
                    )}

                    {/* Classes by Day - Bar Chart */}
                    {activeTab === 2 && data?.classes_by_day && data.classes_by_day.length > 0 && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                    <ClassIcon sx={{ ml: 1 }} />
                                    تعداد هنرجوها بر اساس روز هفته در همه ساعات 
                                </Typography>
                                <ResponsiveContainer width="100%" height={550}>
                                    <BarChart 
                                        data={data.classes_by_day}
                                        margin={{ top: 20, right: 30, left: 80, bottom: 100 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="day_name"
                                            angle={-90}
                                            textAnchor="start"
                                            height={100}
                                            dy={5}
                                            interval={0}
                                        />
                                        <YAxis 
                                            tickFormatter={(value) => toPersianDigits(value)}
                                            width={92}
                                            tickMargin={24}
                                        />
                                        <Tooltip formatter={(value) => toPersianDigits(value)} />
                                        <Legend />
                                        <Bar dataKey="count" fill={chartColors[1]} name="تعداد هنرجو" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Paper>
                        </Grid>
                    )}

                    {/* Monthly Trend - Line Chart */}
                    {activeTab === 3 && filteredMonthlyData && filteredMonthlyData.length > 0 && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                    <TrendingUpIcon sx={{ ml: 1 }} />
                                   روند ماهانه در بازه انتخاب‌شده
                                </Typography>
                                <ResponsiveContainer width="100%" height={400}>
                                    <LineChart 
                                        data={filteredMonthlyData}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="month_name"
                                            angle={-90}
                                            textAnchor="start"
                                            height={100}
                                            dy={5}
                                            interval={0}
                                        />
                                        <YAxis
                                            tickFormatter={(value) => toPersianDigits(value)}
                                            width={92}
                                            tickMargin={24}
                                        />
                                        <Tooltip formatter={(value) => toPersianDigits(value)} />
                                        <Legend />
                                        <Line type="monotone" dataKey="new_enrollments" stroke={chartColors[0]} name="هنرجوهای جدید" />
                                        <Line type="monotone" dataKey="archived_enrollments" stroke={chartColors[1]} name="ثبت‌نام‌های بایگانی‌شده" />
                                    </LineChart>
                                </ResponsiveContainer>

                                <Box sx={{ mt: 4 }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                        <PeopleIcon sx={{ ml: 1 }} />
                                        کل هنرجوهای فعال (تا انتهای هر ماه)
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <LineChart
                                            data={filteredMonthlyData}
                                            margin={{ top: 10, right: 30, left: 20, bottom: 100 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="month_name"
                                                angle={-90}
                                                textAnchor="start"
                                                height={100}
                                                dy={5}
                                                interval={0}
                                            />
                                            <YAxis tickFormatter={(value) => toPersianDigits(value)} width={92} tickMargin={24} />
                                            <Tooltip formatter={(value) => toPersianDigits(value)} />
                                            <Legend />
                                            <Line type="monotone" dataKey="active_students" stroke={chartColors[2]} name="کل هنرجوهای فعال" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Box>
                            </Paper>
                        </Grid>
                    )}

                    {/* Revenue per Course - Bar Chart */}
                    {activeTab === 4 && data?.revenue_by_course && data.revenue_by_course.length > 0 && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                    <AttachMoneyIcon sx={{ ml: 1 }} />
                                    درآمد به ازای مدرس-دوره
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    محاسبه بر اساس بازه انتخاب‌شده (ابتدای «از ماه» تا انتهای «تا ماه»):{' '}
                                    {fromMonth && toMonth
                                        ? `از ${toPersianDigits(moment(fromMonth).format('jMMMM jYYYY'))} تا ${toPersianDigits(moment(toMonth).format('jMMMM jYYYY'))}`
                                        : '-'}
                                    {' '}— مقادیر محور عمودی به میلیون تومان (مبلغ دقیق در راهنمای نمودار)
                                </Typography>
                                <ResponsiveContainer width="100%" height={600}>
                                    <BarChart 
                                        data={data.revenue_by_course.slice(0, 20).map(item => ({
                                            ...item,
                                            teacher_course: `${item.teacher_name || 'بدون مدرس'} - ${item.course_name}`
                                        }))}
                                        margin={{ top: 20, right: 30, left: 80, bottom: 100 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="teacher_course"
                                            angle={-90}
                                            textAnchor="start"
                                            height={150}
                                            dy={10}
                                            interval={0}
                                        />
                                        <YAxis
                                            tickFormatter={(value) => toPersianDigits(value / 1000000)}
                                            width={92}
                                            tickMargin={24}
                                        />
                                        <Tooltip 
                                            formatter={(value) => toPersianDigits(new Intl.NumberFormat('fa-IR').format(value)) + ' تومان'}
                                            labelFormatter={(label, payload) => {
                                                const data = payload && payload[0]?.payload;
                                                if (data && data.teacher_name) {
                                                    return `مدرس: ${data.teacher_name} - دوره: ${label}`;
                                                }
                                                return `دوره: ${label}`;
                                            }}
                                        />
                                        <Legend 
                                            wrapperStyle={{ marginTop: '100px', paddingTop: '100px' }}
                                        />
                                        <Bar dataKey="revenue" fill={chartColors[3]} name="درآمد (تومان)" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Paper>
                        </Grid>
                    )}

                    {/* Attendance Rate per Course - Bar Chart */}
                    {activeTab === 5 && data?.attendance_by_course && data.attendance_by_course.length > 0 && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                    <CheckCircleIcon sx={{ ml: 1 }} />
                                    نرخ حضور به ازای مدرس-دوره
                                </Typography>
                                <ResponsiveContainer width="100%" height={600}>
                                    <BarChart 
                                        data={data.attendance_by_course.slice(0, 20).map(item => ({
                                            ...item,
                                            teacher_course: `${item.teacher_name || 'بدون مدرس'} - ${item.course_name}`
                                        }))}
                                        margin={{ top: 20, right: 30, left: 80, bottom: 100 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="teacher_course"
                                            angle={-90}
                                            textAnchor="start"
                                            height={150}
                                            dy={10}
                                            interval={0}
                                        />
                                        <YAxis 
                                            tickFormatter={(value) => toPersianDigits(value) + '%'}
                                            width={92}
                                            tickMargin={24}
                                        />
                                        <Tooltip 
                                            formatter={(value) => toPersianDigits(value) + '%'}
                                            labelFormatter={(label, payload) => {
                                                const data = payload && payload[0]?.payload;
                                                if (data && data.teacher_name) {
                                                    return `مدرس: ${data.teacher_name} - دوره: ${data.course_name}`;
                                                }
                                                return `دوره: ${label}`;
                                            }}
                                        />
                                        <Legend 
                                            wrapperStyle={{ marginTop: '100px', paddingTop: '100px' }}
                                        />
                                        <Bar dataKey="attendance_rate" fill={chartColors[4]} name="نرخ حضور (%)" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Paper>
                        </Grid>
                    )}

                    {/* Top Courses by Enrollment - Bar Chart */}
                    {activeTab === 6 && data?.top_courses_by_enrollment && data.top_courses_by_enrollment.length > 0 && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                    <PeopleIcon sx={{ ml: 1 }} />
                                    مدرس-دوره‌های پرطرفدار (۱۰ مدرس-دوره برتر)
                                </Typography>
                                <ResponsiveContainer width="100%" height={600}>
                                    <BarChart 
                                        data={data.top_courses_by_enrollment.map(item => ({
                                            ...item,
                                            teacher_course: `${item.teacher_name || 'بدون مدرس'} - ${item.course_name}`
                                        }))}
                                        margin={{ top: 20, right: 30, left: 80, bottom: 100 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="teacher_course"
                                            angle={-90}
                                            textAnchor="start"
                                            height={150}
                                            dy={10}
                                            interval={0}
                                        />
                                        <YAxis 
                                            tickFormatter={(value) => toPersianDigits(value)}
                                            width={92}
                                            tickMargin={24}
                                        />
                                        <Tooltip 
                                            formatter={(value) => toPersianDigits(value)}
                                            labelFormatter={(label, payload) => {
                                                const data = payload && payload[0]?.payload;
                                                if (data && data.teacher_name) {
                                                    return `مدرس: ${data.teacher_name} - دوره: ${data.course_name}`;
                                                }
                                                return `دوره: ${label}`;
                                            }}
                                        />
                                        <Legend 
                                            wrapperStyle={{ marginTop: '100px', paddingTop: '100px' }}
                                        />
                                        <Bar dataKey="enrollment_count" fill={chartColors[2]} name="تعداد ثبت‌نام" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Paper>
                        </Grid>
                    )}

                    {/* Schedule Heatmap */}
                    {activeTab === 7 && data?.schedule_heatmap && data.schedule_heatmap.length > 0 && (() => {
                        // Calculate max count for color scaling
                        const maxCount = Math.max(...data.schedule_heatmap.map(item => item.count || 0));
                        
                        // Transform data into a matrix: day (rows) × hour (columns)
                        const DAY_NAMES = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه'];
                        const hours = Array.from({ length: 15 }, (_, i) => i + 8); // 8 to 22
                        
                        // Create a map for quick lookup: day-hour -> count
                        const dataMap = new Map();
                        data.schedule_heatmap.forEach(item => {
                            const key = `${item.day}-${item.hour}`;
                            dataMap.set(key, item.count || 0);
                        });
                        
                        return (
                            <Grid item xs={12}>
                                <Paper sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                        <ClassIcon sx={{ ml: 1 }} />
                                        Heatmap برنامه‌ریزی (تراکم هنرجوها در هفته)
                                    </Typography>
                                    <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '600px' }}>
                                        <TableContainer>
                                            <Table size="small" sx={{ minWidth: 800 }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell 
                                                            sx={{ 
                                                                position: 'sticky', 
                                                                left: 0, 
                                                                zIndex: 10, 
                                                                backgroundColor: 'background.table',
                                                                fontWeight: 'bold',
                                                                minWidth: 100
                                                            }}
                                                        >
                                                            روز / ساعت
                                                        </TableCell>
                                                        {hours.map(hour => (
                                                            <TableCell 
                                                                key={hour}
                                                                align="center"
                                                                sx={{ 
                                                                    fontWeight: 'bold',
                                                                    minWidth: 60,
                                                                    fontSize: '0.75rem'
                                                                }}
                                                            >
                                                                {`${toPersianDigits(hour)}:۰۰`}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {DAY_NAMES.map((dayName, dayIndex) => (
                                                        <TableRow key={dayIndex}>
                                                            <TableCell 
                                                                sx={{ 
                                                                    position: 'sticky', 
                                                                    left: 0, 
                                                                    zIndex: 10, 
                                                                    backgroundColor: 'background.default',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                {dayName}
                                                            </TableCell>
                                                            {hours.map(hour => {
                                                                const key = `${dayIndex}-${hour}`;
                                                                const count = dataMap.get(key) || 0;
                                                                const cell = heatmapCell(count, maxCount);

                                                                return (
                                                                    <TableCell
                                                                        key={hour}
                                                                        align="center"
                                                                        sx={{
                                                                            backgroundColor: cell.bg,
                                                                            color: cell.color,
                                                                            fontWeight: count > 0 ? 'bold' : 'normal',
                                                                            border: `1px solid ${theme.palette.border.main}`,
                                                                            minWidth: 60,
                                                                            cursor: 'pointer',
                                                                            '&:hover': {
                                                                                opacity: 0.8,
                                                                                transform: 'scale(1.05)',
                                                                                transition: 'all 0.2s'
                                                                            }
                                                                        }}
                                                                        title={`${dayName} ${toPersianDigits(hour)}:۰۰ - ${toPersianDigits(count)} هنرجو`}
                                                                    >
                                                                        {count > 0 ? toPersianDigits(count) : '-'}
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                    {/* Color Legend */}
                                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            تراکم:
                                        </Typography>
                                        {[
                                            { label: 'کم', a: 0.18 },
                                            { label: 'متوسط', a: 0.42 },
                                            { label: 'زیاد', a: 0.66 },
                                            { label: 'خیلی زیاد', a: 0.92 },
                                        ].map((lvl) => (
                                            <Box key={lvl.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ width: 20, height: 20, bgcolor: alpha(theme.palette.primary.main, lvl.a), border: '1px solid', borderColor: 'border.main', borderRadius: '2px' }} />
                                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{lvl.label}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })()}

                    {/* Age Distribution */}
                    {activeTab === 8 && data?.age_distribution && data.age_distribution.overall && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                    <PeopleIcon sx={{ ml: 1 }} />
                                    توزیع سن هنرجویان (کلی)
                                </Typography>
                                <ResponsiveContainer width="100%" height={400}>
                                    <BarChart
                                        data={data.age_distribution.overall}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="age_range"
                                            angle={-90}
                                            textAnchor="start"
                                            height={100}
                                            dy={5}
                                            interval={0}
                                            tickFormatter={(value) => `⁦${toPersianDigits(value)}⁩`}
                                        />
                                        <YAxis
                                            tickFormatter={(value) => toPersianDigits(value)}
                                            width={92}
                                            tickMargin={24}
                                        />
                                        <Tooltip formatter={(value) => toPersianDigits(value)} />
                                        <Legend />
                                        <Bar dataKey="count" fill={chartColors[6]} name="تعداد هنرجو" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Paper>
                        </Grid>
                    )}

                    {/* Similar Courses Comparison */}
                    {activeTab === 9 && data?.similar_courses_comparison && data.similar_courses_comparison.length > 0 && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                    <SchoolIcon sx={{ ml: 1 }} />
                                    مقایسه عملکرد کلاس‌های مشابه
                                </Typography>
                                {data.similar_courses_comparison.map((group, groupIndex) => (
                                    <Box key={groupIndex} sx={{ mb: 4 }}>
                                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                                            {group.course_type} - {toPersianDigits(group.duration)} دقیقه
                                        </Typography>
                                        <StandardTable
                                            maxHeight={false}
                                            getRowKey={(c) => c.course_id}
                                            rows={group.courses || []}
                                            columns={[
                                                { id: 'course_name', label: 'کلاس', align: 'start', render: (c) => c.course_name },
                                                { id: 'teacher_name', label: 'معلم', align: 'start', render: (c) => c.teacher_name },
                                                { id: 'enrollment_count', label: 'تعداد\nثبت‌نام', render: (c) => toPersianDigits(c.enrollment_count) },
                                                { id: 'attendance_rate', label: 'نرخ\nحضور', render: (c) => (
                                                    <Chip
                                                        label={`${toPersianDigits(c.attendance_rate)}%`}
                                                        color={c.attendance_rate >= 80 ? 'success' : c.attendance_rate >= 60 ? 'warning' : 'error'}
                                                        size="small"
                                                    />
                                                ) },
                                                { id: 'revenue', label: 'درآمد', compact: true, render: (c) => `${toPersianDigits(new Intl.NumberFormat('fa-IR').format(c.revenue))} تومان` },
                                                { id: 'retention_rate', label: 'نرخ\nنگهداری', render: (c) => (
                                                    <Chip
                                                        label={`${toPersianDigits(c.retention_rate)}%`}
                                                        color={c.retention_rate >= 80 ? 'success' : 'warning'}
                                                        size="small"
                                                    />
                                                ) },
                                            ]}
                                            footer={(
                                                <TableRow>
                                                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>میانگین</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                        {toPersianDigits(group.averages.enrollment_count)}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                        {toPersianDigits(group.averages.attendance_rate)}%
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                        {toPersianDigits(new Intl.NumberFormat('fa-IR').format(group.averages.revenue))} تومان
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                        {toPersianDigits(group.averages.retention_rate)}%
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        />
                                    </Box>
                                ))}
                            </Paper>
                        </Grid>
                    )}
                </Grid>
                    </Box>
                </Box>
                )}
            </Box>
        </RTL>
    );
}

export default ClassroomInfoGraph;

